import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Wrench, Database } from 'lucide-react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import ImageUploader from './ImageUploader';
import SmartImage from '../SmartImage';

const ServicesManagement = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceImage, setServiceImage] = useState('');

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.SERVICES));
      const servs = [];
      querySnapshot.forEach((doc) => {
        servs.push({ id: doc.id, ...doc.data() });
      });
      setServices(servs);
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const serviceData = {
      name: formData.get('name'),
      price: formData.get('price'),
      duration: formData.get('duration'),
      description: formData.get('description'),
      includes: formData.get('includes')?.split(',').map(i => i.trim()) || [],
      icon: serviceImage || editingService?.icon || '🔧',
      active: true,
      createdAt: Timestamp.now()
    };

    try {
      if (editingService) {
        // التحقق من وجود الخدمة قبل التحديث
        try {
          await updateDoc(doc(db, COLLECTIONS.SERVICES, editingService.id), serviceData);
        } catch (updateError) {
          if (updateError.code === 'not-found') {
            alert('هذه الخدمة غير موجودة في قاعدة البيانات. سيتم إضافتها كخدمة جديدة.');
            await addDoc(collection(db, COLLECTIONS.SERVICES), serviceData);
          } else {
            throw updateError;
          }
        }
      } else {
        await addDoc(collection(db, COLLECTIONS.SERVICES), serviceData);
      }
      setShowModal(false);
      setEditingService(null);
      setServiceImage('');
      loadServices();
    } catch (error) {
      console.error('Error saving service:', error);
      alert(`حدث خطأ أثناء حفظ الخدمة: ${error.message}`);
    }
  };

  const handleDelete = async (serviceId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الخدمة؟')) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.SERVICES, serviceId));
        // حذف محلي لتحديث الواجهة فوراً
        setServices(prev => prev.filter(s => s.id !== serviceId));
      } catch (error) {
        console.error('Error deleting service:', error);
        if (error.code === 'not-found') {
          // إذا لم تكن الخدمة موجودة في Firebase، نزيلها من الحالة المحلية فقط
          setServices(prev => prev.filter(s => s.id !== serviceId));
          alert('تم حذف الخدمة من الواجهة (لم تكن موجودة في قاعدة البيانات).');
        } else {
          alert(`حدث خطأ أثناء حذف الخدمة: ${error.message}`);
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة الخدمات</h1>
          <p className="text-gray-600 mt-2">إجمالي الخدمات: {services.length}</p>
        </div>
        <button
          onClick={() => {
            setEditingService(null);
            setServiceImage('');
            setShowModal(true);
          }}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          إضافة خدمة جديدة
        </button>
      </div>

      {services.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <div key={service.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
              <div className="bg-gradient-to-r from-blue-500 to-cyan-500 p-6 text-white">
                <div className="w-20 h-20 mb-3 mx-auto flex items-center justify-center">
                  <SmartImage
                    src={service.icon}
                    alt={service.name}
                    size="large"
                    fallback="🔧"
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <h3 className="text-xl font-bold mb-2">{String(service.name || '')}</h3>
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-bold">{String(service.price || '')}</p>
                  <p className="text-sm opacity-90">{String(service.duration || '')}</p>
                </div>
              </div>

              <div className="p-6">
                {service.description && (
                  <p className="text-gray-600 text-sm mb-4">{String(service.description)}</p>
                )}

                {service.includes && service.includes.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-bold text-gray-700 mb-2">يشمل:</p>
                    <ul className="text-sm text-gray-600 space-y-1">
                      {service.includes.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <span className="text-green-500">✓</span>
                          {String(item)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingService(service);
                      setServiceImage(service.icon || '');
                      setShowModal(true);
                    }}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    تعديل
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    حذف
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <Wrench className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا توجد خدمات متاحة</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingService ? 'تعديل الخدمة' : 'إضافة خدمة جديدة'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">اسم الخدمة</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingService?.name}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">السعر</label>
                  <input
                    type="text"
                    name="price"
                    defaultValue={editingService?.price}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">المدة</label>
                  <input
                    type="text"
                    name="duration"
                    defaultValue={editingService?.duration}
                    placeholder="مثال: ساعة واحدة"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                  />
                </div>
              </div>

              <ImageUploader
                currentImage={serviceImage || editingService?.icon}
                onImageChange={setServiceImage}
                label="صورة أو رمز الخدمة"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الوصف</label>
                <textarea
                  name="description"
                  defaultValue={editingService?.description}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">ما تشمله الخدمة (افصل بفاصلة)</label>
                <input
                  type="text"
                  name="includes"
                  defaultValue={editingService?.includes?.join(', ')}
                  placeholder="فحص المحرك, فحص الفرامل, فحص الإطارات"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {editingService ? 'حفظ التعديلات' : 'إضافة الخدمة'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingService(null);
                    setServiceImage('');
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServicesManagement;
